import { type OptionsValue, OTHER_ID } from '../../../types/form/form-fields/field-values';
import type { ConstraintFunction } from '../../../types/form/form-section/config';

export const optionsValueEqualsCheck: ConstraintFunction<OptionsValue> = (
	value: OptionsValue | undefined,
	constraint: string[],
) => {
	if (value === undefined) {
		return false;
	}
	const idSet = new Set([
		...(value.options.map((item) => item.id) || []),
		...(value.otherOptionText !== undefined ? [OTHER_ID] : []),
	]);

	if (idSet.size !== constraint.length) {
		return false;
	}
	return constraint.every((option) => idSet.has(option));
};

export const optionsValueSomeOfCheck: ConstraintFunction<OptionsValue> = (
	value: OptionsValue | undefined,
	constraint: string[],
) => {
	if (value === undefined) {
		return false;
	}
	const idSet = new Set([
		...(value.options.map((item) => item.id) || []),
		...(value.otherOptionText !== undefined ? [OTHER_ID] : []),
	]);
	return constraint.some((option) => idSet.has(option));
};

export const optionsValueNoneOfCheck: ConstraintFunction<OptionsValue> = (
	value: OptionsValue | undefined,
	constraint: string[],
) => {
	if (value === undefined) {
		return true;
	}
	const idSet = new Set([
		...(value.options.map((item) => item.id) || []),
		...(value.otherOptionText !== undefined ? [OTHER_ID] : []),
	]);
	return !constraint.some((option) => idSet.has(option));
};

/**
 * Cascading fields have a different data structure. The parent field value and
 * child field value are concatenated together with `:` eg `1:3`
 * A check constraint can match on either the `1` or `1:3`
 * If a user picked `1` as a constraint, this should match on `1` and `1:3` since
 * 1 has been picked by the user in both situations
 * But it shouldn't match if the user selected `3`, only `1` and `1:3` are valid values
 */

export const cascadingValueNoneOfCheck: ConstraintFunction<OptionsValue> = (
	value: OptionsValue | undefined,
	constraint: string[],
) => !cascadingValueSomeOfCheck(value, constraint);

export const cascadingValueSomeOfCheck: ConstraintFunction<OptionsValue> = (
	value: OptionsValue | undefined,
	constraint: string[],
) => {
	if (value === undefined) {
		return false;
	}
	const idSet = new Set(value.options.flatMap((item) => [item.id, item.id.split(':')[0]]));
	return constraint.some((option) => idSet.has(option));
};

export const optionsValueEmptyCheck: ConstraintFunction<OptionsValue> = (
	value: OptionsValue | undefined,
) => value === undefined || (value.options.length === 0 && !value.otherOptionText);

export const optionsValueNotEmptyCheck: ConstraintFunction<OptionsValue> = (
	value: OptionsValue | undefined,
) => value !== undefined && (value.options.length > 0 || !!value.otherOptionText);
